import {
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/system'
import { selectTitles } from '../../../../../redux/features/titles/titlesSlice'
import { selectDepartments } from '../../../../../redux/features/departments/departmentsSlice'
import OfoqAutoComplete from '../../../../../components/Forms/OfoqAutoComplete.jsx'
import OfoqTextField from '../../../../../components/Forms/OfoqTextField.jsx'
import completeInfoImage from '../../../../../images/complete-profile-art.svg'
import OfoqPasswordField from '../../../../../components/Forms/OfoqPasswordField.jsx'
import { customerAdminSelectUsers } from '../../../../../redux/features/users/customerAdmin/customerAdminSlice'
import { customerAdminUpdateSelectedUser } from '../../../../../redux/features/users/customerAdmin/customerAdminThunk'
import { selectEmployees } from '../../../../../redux/features/employees/employeesSlice'
import { selectAuthUser } from '../../../../../redux/features/userAuth/userAuthSlice'
import { isNotEmpty, objNotEmpty } from '../../../../../utils/helpers'
import CookiesStorageService from '../../../../../services/CookiesStorageService'
import retrieveDepartments from '../../../../../redux/features/departments/departmentsThunk'
import { getEmployeesWithPagination, retrieveEmployees, searchEmployees } from '../../../../../redux/features/employees/employeesThunk'
import UseTranslation from '../../../../../hooks/UseTranslation.jsx'
import OfoqMultiAutoCompleteWithLoadMore from '../../../../../components/Forms/OfoqMultiAutoCompleteWithLoadMore.jsx'
import { ReactComponent as TeamSnap } from '../../../../../images/TeamSnap.svg'

const UserStepOne = ({ setValidStepOne }) => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const { updatedUser } = useSelector(customerAdminSelectUsers)
  const { authorizedUser } = useSelector(selectAuthUser)
  const { t } = UseTranslation()
  const titlesStore = useSelector(selectTitles)
  const departmentStore = useSelector(selectDepartments)
  const employeesStore = useSelector(selectEmployees)

  const [title, setTitle] = useState(updatedUser?.title)
  const [departments, setDepartments] = useState(updatedUser?.departments)
  const [employee, setEmployee] = useState(updatedUser?.employee)
  const [name, setName] = useState(updatedUser?.name)
  const [password, setPassword] = useState(updatedUser?.password)
  const [email, setEmail] = useState(updatedUser?.email)
  const [language, setLanguage] = useState(updatedUser?.language || 'ENGLISH')
  const [validPassword, setValidPassword] = useState(true)
  const [company, setCompany] = useState(updatedUser?.company)
  const integrationType = CookiesStorageService.getIntegrationType()
  useEffect(() => {
    if (integrationType === 'STANDALONE') {
      dispatch(retrieveDepartments({ queries: company?.tenant.id }))
      if (!id) setEmployee(null)
    } else {
      dispatch(retrieveEmployees({ queries: company?.tenant.id }))
      setDepartments(null)
      if (!id) setEmployee(null)
    }
  }, [company])

  useEffect(() => {
    setTitle(updatedUser?.title)
    setDepartments(updatedUser?.departments)
    setEmployee(updatedUser?.employee)
    setName(updatedUser?.name)
    setPassword(updatedUser?.password)
    setEmail(updatedUser?.email)
    setLanguage(updatedUser?.language || 'ENGLISH')
    setCompany(updatedUser?.company)
  }, [updatedUser])

  useEffect(() => {
    dispatch(
      customerAdminUpdateSelectedUser({
        queries: {
          ...updatedUser,
          title,
          departments,
          employee,
          name,
          password,
          email,
          language,
          tenant_id: authorizedUser.tenant_id,
        },
      }),
    )

    if (email && validPassword && name && title && language) {
      setValidStepOne(true)
    } else {
      setValidStepOne(false)
    }
  }, [title, departments, employee, name, password, email, language, validPassword])

  const employeeNameConverter = (item) => {
    if (item.code) {
      return `${item.code} - ${item.english_name}`
    }
    return item.english_name
  }

  const handleEmployeeChange = (user) => {
    setEmployee(user)
    if (!isNotEmpty(id) && objNotEmpty(user)) {
      setName(user?.english_name || '')
      setEmail(user?.email || '')
    }
  }

  return (
    <Container maxWidth='xl'>
      <Grid container justify='space-between'>
        <Grid item xs={12} md={8} lg={9} container sx={styles.section}>
          <Grid
            item
            xs={12}
            sx={styles.sectionContainer}>
            <Box
              sx={{
                display: 'block',
              }}>
              <Typography sx={styles.sectionTitle} variant='h6'>
              {t?.['Basic Information']}
              </Typography>
              <Typography sx={styles.sectionSubTitle} variant='h6'>
                {t?.['This section defines the main information about the user.']}
              </Typography>
            </Box>
          </Grid>
          <Divider
            sx={styles.dividerStyle}
          />

          <Grid container sx={styles.inputsContainerFixSize}>
            <Grid item xs={12} sm={6} sx={styles.inputsGroupFixSize}>
              <OfoqAutoComplete
                name={t?.Title}
                defaultOption={title}
                refState={setTitle}
                optionsConfig={titlesStore?.value}
                required
                customStyle={styles.autoCompleteStyle}
              />
            </Grid>

            {CookiesStorageService.getIntegrationType() !== 'STANDALONE' && <>
              <Grid item xs={12} sm={6} sx={styles.inputsGroupFixSize}>
               <OfoqMultiAutoCompleteWithLoadMore
                  name={t?.Employee}
                  store={employeesStore?.value}
                  defaultOption={employee}
                  refState={handleEmployeeChange}
                  nameConverter={employeeNameConverter}
                  required
                  loadMoreActionToDispatch={getEmployeesWithPagination}
                  searchActionToDispatch={searchEmployees}
                  customPlaceHolder={t?.['an Employee']}
                  customSearchPlaceholder={t?.['Search for Employee by Code or Name']}
                  multiple={false}
                  reduxStore={employeesStore}
                />
              </Grid>
              <Grid item xs={12} sx={styles.inputsGroupFixSize}>
                <OfoqTextField
                  name={t?.Name}
                  placeholder={t?.['Type Employee Name']}
                  defaultValue={name}
                  refState={setName}
                  type='text'
                  required
                />
              </Grid>
            </>}

            {CookiesStorageService.getIntegrationType() === 'STANDALONE' && <>
              <Grid item xs={12} sm={6} style={styles.inputsGroupFixSize}>
                <OfoqTextField
                  name={t?.Employee}
                  placeholder={t?.['Type Employee']}
                  defaultValue={employee?.code || employee}
                  refState={setEmployee}
                />
              </Grid>
              <Grid item xs={12} sm={6} style={styles.inputsGroupFixSize}>
                <OfoqTextField
                    name={t?.Name}
                  placeholder={t?.['Type Employee Name']}
                  defaultValue={name}
                  refState={setName}
                  type='text'
                  required
                />
              </Grid>
              <Grid item xs={12} style={styles.inputsGroupFixSize}>
                <OfoqAutoComplete
                  name={t?.['Manager of Department']}
                  defaultOption={departments}
                  refState={setDepartments}
                  optionsConfig={departmentStore?.value}
                  disabled={!isNotEmpty(employee)}
                  multiple
                />
              </Grid>
            </>}

            <>
              <Grid item xs={12} style={styles.inputsGroupFixSize}>
                <OfoqTextField
                  name={t?.Email}
                  placeholder={t?.['Type the user email']}
                  defaultValue={email}
                  refState={setEmail}
                  type='email'
                  required
                />
              </Grid>
              {!id && (
                <Grid item xs={12} style={styles.inputsGroupFixSize}>
                  <OfoqPasswordField
                    name={t?.Password}
                    placeholder={t?.['Type the user password']}
                    defaultValue={password}
                    refState={setPassword}
                    setValidPassword={setValidPassword}
                    required
                  />
                </Grid>
              )}
              <Grid item xs={12} style={styles.inputsGroupFixSize}>
                <FormControl fullWidth>
                  <InputLabel htmlFor='language' sx={styles.formInputLabelRequired}>
                    {t?.language}  <TeamSnap />
                  </InputLabel>
                </FormControl>
                <RadioGroup style={styles.radioGroup}>
                <Grid item xs={6} sm={6}>
                  <FormControl fullWidth component='fieldset'>
                    <FormControlLabel
                      required
                      fullWidth
                      control={<Radio sx={styles.radioIcon} value={language} checked={language === 'ENGLISH'} />}
                      value='ENGLISH'
                      name='language'
                      id='language'
                      label={t?.English}
                      variant='outlined'
                      onChange={() => setLanguage('ENGLISH')}
                      sx={
                        language === 'ENGLISH'
                          ? styles.activeRadioContainer
                          : styles.radioContainer
                      }
                      InputProps={{
                        autoComplete: 'new-name',
                        form: {
                          autoComplete: 'off',
                        },
                      }}></FormControlLabel>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={6}>
                  <FormControl fullWidth component='fieldset'>
                    <FormControlLabel
                      required
                      fullWidth
                      control={<Radio sx={styles.radioIcon} value={language} checked={language === 'ARABIC'} />}
                      value='ARABIC'
                      name='language'
                      id='language'
                      label={t?.Arabic}
                      variant='outlined'
                      onChange={() => setLanguage('ARABIC')}
                      sx={
                        language === 'ARABIC'
                          ? styles.activeRadioContainer
                          : styles.radioContainer
                      }
                      InputProps={{
                        autoComplete: 'new-name',
                        form: {
                          autoComplete: 'off',
                        },
                      }}></FormControlLabel>
                  </FormControl>
                </Grid>

                </RadioGroup>
              </Grid>
            </>
          </Grid>

        </Grid>

        <Grid item md={4} lg={3} sx={styles.ImageContainer}>
          <img src={completeInfoImage} alt='complete-profile-art' />
        </Grid>
      </Grid>
    </Container>
  )
}

const styles = {
  section: {
    boxShadow: '0 1px 3px 0 #cfd8dc, 0 0 0 1px #f1f1f1',
    display: 'block',
    padding: { xs: '10px', sm: '20px' },
    borderRadius: 5,
    background: '#fff',
    height: '100%',
  },
  sectionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'baseline', sm: 'normal' },
    p: '5px 10px',
  },
  sectionTitle: {
    fontFamily: 'Avenir 85 Heavy',
    fontWeight: 500,
    fontSize: { xs: '14px', sm: '20px' },
  },
  sectionSubTitle: {
    fontFamily: 'Avenir',
    fontWeight: 500,
    fontSize: { xs: '12px', sm: '16px' },
    lineHeight: 1,
    letterSpacing: 0.1,
    color: '#98A9BC',
    marginTop: '5px',
  },
  formInputLabelRequired: {
    display: 'flex',
    alignItems: 'center',
    transform: 'translate(0,0) scale(1)',
    color: '#000',
    fontSize: { xs: 12, sm: 16 },
    '& svg': {
      mx: '10px',
    },
  },
  radioGroup: {
    flexDirection: 'row',
    width: '100%',
  },
  radioIcon: {
    '&.Mui-checked': {
      color: '#fff',
    },
  },
  activeRadioContainer: {
    width: '85%',
    height: { xs: '15px', sm: '20px' },
    margin: '4px 14px 16px 8px',
    padding: { xs: '8px', sm: '16px' },
    borderRadius: '8px',
    border: 'solid 2px #e2e8ee',
    backgroundColor: '#29cc8b',
    color: '#ffffff',
    '& .MuiIconButton-label': {
      color: '#ffffff !important',
    },
  },
  radioContainer: {
    width: '85%',
    height: { xs: '15px', sm: '20px' },
    margin: '4px 14px 16px 8px',
    padding: { xs: '8px', sm: '16px' },
    borderRadius: '8px',
    border: 'solid 2px #e2e8ee',
    backgroundColor: '#fafbfc',
  },
  dividerStyle: {
    my: { xs: '5px', sm: '10px' },
  },
  inputsContainerFixSize: {
    display: 'flex',
  },
  inputsGroupFixSize: {
    padding: '5px 10px',
  },
  ImageContainer: {
    display: { xs: 'none', md: 'block' },
    maxWidth: '100%',
    '& img': {
      maxWidth: '100%',
      height: '550px',
      margin: 'auto',
      display: 'block',
    },
  },
  autoCompleteStyle: {
    marginBottom: '0px !important',
  },
}
export default UserStepOne
